import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import GradientOutlinedIcon from "@mui/icons-material/GradientOutlined";
import OpacityOutlinedIcon from "@mui/icons-material/OpacityOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TransformOutlinedIcon from "@mui/icons-material/TransformOutlined";
import AlignHorizontalLeftOutlinedIcon from "@mui/icons-material/AlignHorizontalLeftOutlined";
import AlignHorizontalCenterOutlinedIcon from "@mui/icons-material/AlignHorizontalCenterOutlined";
import AlignHorizontalRightOutlinedIcon from "@mui/icons-material/AlignHorizontalRightOutlined";
import AlignVerticalTopOutlinedIcon from "@mui/icons-material/AlignVerticalTopOutlined";
import AlignVerticalCenterOutlinedIcon from "@mui/icons-material/AlignVerticalCenterOutlined";
import AlignVerticalBottomOutlinedIcon from "@mui/icons-material/AlignVerticalBottomOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import JoinFullOutlinedIcon from "@mui/icons-material/JoinFullOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import FormatAlignLeftOutlinedIcon from "@mui/icons-material/FormatAlignLeftOutlined";
import FormatAlignCenterOutlinedIcon from "@mui/icons-material/FormatAlignCenterOutlined";
import FormatAlignRightOutlinedIcon from "@mui/icons-material/FormatAlignRightOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import {
  MARK_FONT_SIZE_MAX,
  MARK_FONT_SIZE_MIN,
  GRID_COLS_MAX,
  GRID_COLS_MIN,
  GRID_ROWS_MAX,
  GRID_ROWS_MIN,
  POLAR_RAYS_MAX,
  POLAR_RAYS_MIN,
  POLAR_RINGS_MAX,
  POLAR_RINGS_MIN,
  clampMarkFontSize,
  clampGridCols,
  clampGridRows,
  clampPolarRings,
  clampPolarRays,
  markFontGroups,
} from "./skillAgentMoodboardLayout";

const VC = {
  panel: "#181b22",
  panelBorder: "#2a2f3a",
  icon: "#c5c9d2",
  iconDim: "#6b7280",
  hover: "rgba(255,255,255,0.08)",
  activeBg: "rgba(217,71,47,0.22)",
  accent: "#d9472f",
  text: "#e8eaed",
  textDim: "#9aa0a8",
};

const DOCK_W = 40;
const PANEL_W = 240;

/**
 * Rail derecho (prioridad CRM): Color + Gradient arriba.
 * Color Guide de VC era casi igual a Color → unificado.
 */
export const VC_DOCK_GROUPS = [
  [
    { id: "color", label: "Color", Icon: PaletteOutlinedIcon },
    { id: "gradient", label: "Gradient", Icon: GradientOutlinedIcon },
    { id: "swatches", label: "Swatches", Icon: StyleOutlinedIcon },
  ],
  [
    { id: "stroke", label: "Stroke", Icon: BorderColorOutlinedIcon },
    { id: "transparency", label: "Transparency", Icon: OpacityOutlinedIcon },
    { id: "brushes", label: "Brushes", Icon: BrushOutlinedIcon },
  ],
  [
    { id: "symbols", label: "Symbols", Icon: AutoAwesomeOutlinedIcon },
    {
      id: "patternOptions",
      label: "Pattern Options",
      Icon: GridOnOutlinedIcon,
    },
  ],
  [
    { id: "appearance", label: "Appearance", Icon: CategoryOutlinedIcon },
    { id: "graphicStyles", label: "Graphic Styles", Icon: TuneOutlinedIcon },
  ],
  [{ id: "artboards", label: "Artboards", Icon: DashboardOutlinedIcon }],
  [
    { id: "transform", label: "Transform", Icon: TransformOutlinedIcon },
    { id: "align", label: "Align", Icon: AlignHorizontalLeftOutlinedIcon },
    { id: "pathfinder", label: "Pathfinder", Icon: JoinFullOutlinedIcon },
  ],
  [
    { id: "character", label: "Character", Icon: TitleOutlinedIcon },
    { id: "paragraph", label: "Paragraph", Icon: FormatAlignLeftOutlinedIcon },
    { id: "glyphs", label: "Glyphs", Icon: TextFieldsOutlinedIcon },
  ],
  [
    { id: "history", label: "History", Icon: HistoryOutlinedIcon },
    { id: "actions", label: "Actions", Icon: PlayArrowOutlinedIcon },
    { id: "info", label: "Info", Icon: InfoOutlinedIcon },
    { id: "navigator", label: "Navigator", Icon: MapOutlinedIcon },
  ],
  [{ id: "variables", label: "Variables", Icon: BarChartOutlinedIcon }],
];

const SWATCHES = [
  "#111827",
  "#e11d48",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ffffff",
  "#94a3b8",
  "#fef3c7",
];

/**
 * Dock derecho VectorCraft: rail de iconos + panel expandido.
 */
export default function MoodboardVcRightDock({
  panel = null,
  onChangePanel,
  selectionLabel = "",
  transform = null,
  onPatchTransform,
  stroke = 2,
  onChangeStroke,
  fillColor = "#e11d48",
  strokeColor = "#e11d48",
  opacity = 1,
  onChangeFill,
  onChangeStrokeColor,
  onChangeOpacity,
  colorTarget = "fill",
  onChangeColorTarget,
  onSwapFillStroke,
  onApplyGradient,
  /** { from, to, kind, angle } — panel Gradient VectorCraft */
  gradient = null,
  onChangeGradient,
  onReverseGradient,
  onNoneFill,
  layerItems = [],
  onSelectLayer,
  pageCount = 1,
  pageIdx = 0,
  onSelectPage,
  /** (fromIndex, toIndex) — reorder pages[] (= PDF order) */
  onReorderPage,
  /** Relayout board poses in current pages[] order */
  onRearrangeArtboards,
  onAlign,
  onDistribute,
  /** selection | page | key — VectorCraft Align To */
  alignTo = "selection",
  onChangeAlignTo,
  alignSpacing = 0.02,
  onChangeAlignSpacing,
  onPathfinder,
  fontSize = 14,
  fontFamily = "sans",
  fontWeight = 700,
  textAlign = "left",
  onChangeFontSize,
  onChangeFontFamily,
  onChangeFontWeight,
  onChangeTextAlign,
  /** Marca seleccionada (grid/polar) para editar divisiones */
  selectedMark = null,
  onPatchSelectedMark,
  /** Liquify: radio / fuerza / ángulo twirl */
  liquifyOpts = null,
  onChangeLiquifyOpts,
  /** Anchura: paso del stroke */
  widthOpts = null,
  onChangeWidthOpts,
  /** Envelope › Make with Warp */
  envelopeOpts = null,
  onChangeEnvelopeOpts,
  onApplyEnvelopeWarp,
  onApplyEnvelopeMesh,
  /** Perspective Grid */
  perspectiveGrid = null,
  onChangePerspectiveGrid,
  onTogglePerspectiveGrid,
  /** Symbols / Sprayer */
  symbols = null,
  activeSymbolId = null,
  onSelectSymbol,
  onDefineSymbol,
  symbolSprayOpts = null,
  onChangeSymbolSprayOpts,
  /** Suavizar: pasadas */
  smoothOpts = null,
  onChangeSmoothOpts,
  /** Varita mágica: tolerancia */
  wandOpts = null,
  onChangeWandOpts,
  /** Herramienta de marca activa (para mostrar panel liquify) */
  activeMarkTool = null,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  infoLines = [],
}) {
  const open = panel != null;
  const [local, setLocal] = useState({});
  const title = useMemo(() => {
    for (const g of VC_DOCK_GROUPS) {
      const hit = g.find((p) => p.id === panel);
      if (hit) return hit.label;
    }
    return panel || "";
  }, [panel]);

  const syncField = (key, value) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const commitNum = (key, raw, scale = 1) => {
    const n = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(n)) return;
    onPatchTransform?.({ [key]: n * scale });
    setLocal((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const pct = (v) =>
    Number.isFinite(Number(v)) ? (Number(v) * 100).toFixed(1) : "";
  const deg = (v) =>
    Number.isFinite(Number(v)) ? String(Math.round(Number(v))) : "";
  const fieldVal = (key, display) =>
    local[key] != null ? local[key] : display;

  const hexOk = (h) => (/^#[0-9a-fA-F]{6}$/i.test(h) ? h : "#e11d48");

  const DockBtn = ({ id, Icon, label }) => {
    const active = panel === id;
    return (
      <Tooltip title={label} placement="left">
        <IconButton
          size="small"
          onClick={() => onChangePanel?.(active ? null : id)}
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1,
            color: active ? VC.accent : VC.icon,
            bgcolor: active ? VC.activeBg : "transparent",
            "&:hover": { bgcolor: active ? VC.activeBg : VC.hover },
          }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    );
  };

  const sectionLabel = (t) => (
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 800,
        color: VC.textDim,
        textTransform: "uppercase",
        letterSpacing: 0.05,
        mt: 0.5,
      }}
    >
      {t}
    </Typography>
  );

  const soon = (msg) => (
    <Typography
      sx={{ fontSize: "0.72rem", color: VC.textDim, lineHeight: 1.4 }}
    >
      {msg ||
        "Panel de referencia VectorCraft — cableado parcial en moodboard."}
    </Typography>
  );

  const fillHex = hexOk(fillColor === "none" ? "#ffffff" : fillColor);
  const strokeHex = hexOk(strokeColor);
  const target = colorTarget === "stroke" ? "stroke" : "fill";
  const activeHex = target === "stroke" ? strokeHex : fillHex;

  /** Fill/Stroke + picker + swap/none (antes en la barra izquierda). */
  const colorPickers = (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}
        >
          <Tooltip title="Stroke">
            <Box
              component="button"
              type="button"
              onClick={() => onChangeColorTarget?.("stroke")}
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 24,
                height: 24,
                p: 0,
                borderRadius: 0.5,
                border:
                  target === "stroke"
                    ? `2px solid ${VC.accent}`
                    : `1px solid ${alpha("#fff", 0.35)}`,
                bgcolor: strokeHex,
                cursor: "pointer",
                zIndex: target === "stroke" ? 2 : 1,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 5,
                  bgcolor: VC.panel,
                  borderRadius: 0.25,
                  border: `1px solid ${alpha("#fff", 0.12)}`,
                },
              }}
            />
          </Tooltip>
          <Tooltip title="Fill">
            <Box
              component="button"
              type="button"
              onClick={() => onChangeColorTarget?.("fill")}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 24,
                height: 24,
                p: 0,
                borderRadius: 0.5,
                border:
                  target === "fill"
                    ? `2px solid ${VC.accent}`
                    : `1px solid ${alpha("#fff", 0.35)}`,
                bgcolor: fillColor === "none" ? "transparent" : fillHex,
                backgroundImage:
                  fillColor === "none"
                    ? "linear-gradient(45deg,#666 25%,transparent 25%),linear-gradient(-45deg,#666 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#666 75%),linear-gradient(-45deg,transparent 75%,#666 75%)"
                    : undefined,
                backgroundSize: fillColor === "none" ? "6px 6px" : undefined,
                cursor: "pointer",
                zIndex: target === "fill" ? 2 : 1,
              }}
            />
          </Tooltip>
        </Box>
        <Box
          component="label"
          title={target === "stroke" ? "Color stroke" : "Color fill"}
          sx={{
            position: "relative",
            flex: 1,
            height: 28,
            borderRadius: 1,
            border: `1px solid ${VC.accent}`,
            bgcolor:
              fillColor === "none" && target === "fill" ? VC.panel : activeHex,
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <Box
            component="input"
            type="color"
            value={activeHex}
            onChange={(e) => {
              const hex = e.target.value;
              if (target === "stroke") onChangeStrokeColor?.(hex);
              else onChangeFill?.(hex);
            }}
            sx={{
              position: "absolute",
              inset: -6,
              width: "140%",
              height: "140%",
              opacity: 0,
              cursor: "pointer",
              border: "none",
            }}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Swap fill ↔ stroke">
          <IconButton
            size="small"
            onClick={() => onSwapFillStroke?.()}
            sx={{ color: VC.icon, bgcolor: alpha("#fff", 0.04) }}
          >
            <SwapVertOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="None (sin fill)">
          <IconButton
            size="small"
            onClick={() => {
              onChangeColorTarget?.("fill");
              onNoneFill?.();
            }}
            sx={{ color: VC.icon, bgcolor: alpha("#fff", 0.04) }}
          >
            <BlockOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: VC.textDim,
            alignSelf: "center",
            ml: 0.5,
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          {target}
        </Typography>
      </Stack>
    </Stack>
  );

  const swatchGrid = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 0.5,
      }}
    >
      {SWATCHES.map((hex) => (
        <Box
          key={hex}
          component="button"
          type="button"
          title={
            target === "stroke"
              ? `${hex} · stroke (clic) · fill (derecho)`
              : `${hex} · fill (clic) · stroke (derecho)`
          }
          onClick={() => {
            // Respeta Fill/Stroke activo (antes clic siempre pisaba fill).
            if (target === "stroke") onChangeStrokeColor?.(hex);
            else onChangeFill?.(hex);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            if (target === "stroke") onChangeFill?.(hex);
            else onChangeStrokeColor?.(hex);
          }}
          sx={{
            width: "100%",
            aspectRatio: "1",
            p: 0,
            border:
              (target === "stroke" ? hexOk(strokeColor) : hexOk(fillColor)) ===
              hex
                ? `2px solid ${VC.accent}`
                : `1px solid ${VC.panelBorder}`,
            borderRadius: 0.5,
            bgcolor: hex,
            cursor: "pointer",
          }}
        />
      ))}
    </Box>
  );

  const transformFields = transform ? (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 0.75,
      }}
    >
      {[
        ["x", "X %", pct(transform.x), 0.01],
        ["y", "Y %", pct(transform.y), 0.01],
        ["w", "W %", pct(transform.w), 0.01],
        ["h", "H %", pct(transform.h), 0.01],
      ].map(([key, label, display, scale]) => (
        <TextField
          key={key}
          size="small"
          label={label}
          value={fieldVal(key, display)}
          onChange={(e) => syncField(key, e.target.value)}
          onBlur={() => commitNum(key, fieldVal(key, display), scale)}
          onKeyDown={(e) => {
            if (e.key === "Enter")
              commitNum(key, fieldVal(key, display), scale);
          }}
          InputProps={{ sx: { fontSize: "0.75rem", color: VC.text } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: alpha("#fff", 0.04),
              "& fieldset": { borderColor: VC.panelBorder },
            },
            "& label": { color: VC.textDim },
          }}
        />
      ))}
      <TextField
        size="small"
        label="Rot °"
        value={fieldVal("rotation", deg(transform.rotation))}
        onChange={(e) => syncField("rotation", e.target.value)}
        onBlur={() =>
          commitNum(
            "rotation",
            fieldVal("rotation", deg(transform.rotation)),
            1,
          )
        }
        onKeyDown={(e) => {
          if (e.key === "Enter")
            commitNum(
              "rotation",
              fieldVal("rotation", deg(transform.rotation)),
              1,
            );
        }}
        InputProps={{ sx: { fontSize: "0.75rem", color: VC.text } }}
        sx={{
          gridColumn: "1 / -1",
          "& .MuiOutlinedInput-root": {
            bgcolor: alpha("#fff", 0.04),
            "& fieldset": { borderColor: VC.panelBorder },
          },
          "& label": { color: VC.textDim },
        }}
      />
    </Box>
  ) : (
    soon("Selecciona una marca o foto.")
  );

  const alignToMode = ["selection", "page", "key"].includes(alignTo)
    ? alignTo
    : "selection";

  const alignBody = (
    <Stack spacing={1.25}>
      {sectionLabel("Align To")}
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {[
          ["selection", "Selection"],
          ["page", "Page"],
          ["key", "Key object"],
        ].map(([id, tip]) => (
          <Tooltip key={id} title={tip}>
            <Box
              component="button"
              type="button"
              onClick={() => onChangeAlignTo?.(id)}
              sx={{
                px: 0.85,
                py: 0.35,
                borderRadius: 1,
                border: "1px solid",
                borderColor: alignToMode === id ? VC.accent : VC.panelBorder,
                bgcolor:
                  alignToMode === id
                    ? alpha(VC.accent, 0.22)
                    : alpha("#fff", 0.04),
                color: VC.text,
                fontSize: "0.65rem",
                fontWeight: 700,
                cursor: "pointer",
                textTransform: "none",
              }}
            >
              {id === "selection" ? "Sel" : id === "page" ? "Page" : "Key"}
            </Box>
          </Tooltip>
        ))}
      </Stack>
      {alignToMode === "key" ? (
        <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
          El último seleccionado (activo) es la clave: no se mueve.
        </Typography>
      ) : null}
      {sectionLabel("Align Objects")}
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {[
          ["left", AlignHorizontalLeftOutlinedIcon, "Left"],
          ["hcenter", AlignHorizontalCenterOutlinedIcon, "H Center"],
          ["right", AlignHorizontalRightOutlinedIcon, "Right"],
          ["top", AlignVerticalTopOutlinedIcon, "Top"],
          ["vcenter", AlignVerticalCenterOutlinedIcon, "V Center"],
          ["bottom", AlignVerticalBottomOutlinedIcon, "Bottom"],
        ].map(([edge, Icon, tip]) => (
          <Tooltip key={edge} title={tip}>
            <IconButton
              size="small"
              onClick={() => onAlign?.(edge)}
              sx={{
                color: VC.icon,
                bgcolor: alpha("#fff", 0.04),
                "&:hover": { color: VC.accent, bgcolor: VC.hover },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ))}
      </Stack>
      {sectionLabel("Distribute")}
      <Stack direction="row" gap={0.5}>
        <Tooltip title="Horizontal spacing">
          <IconButton
            size="small"
            onClick={() => onDistribute?.("x")}
            sx={{ color: VC.icon, bgcolor: alpha("#fff", 0.04) }}
          >
            <SwapHorizOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Vertical spacing">
          <IconButton
            size="small"
            onClick={() => onDistribute?.("y")}
            sx={{ color: VC.icon, bgcolor: alpha("#fff", 0.04) }}
          >
            <SwapVertOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      {sectionLabel("Distribute Spacing")}
      <Stack direction="row" spacing={1} alignItems="center">
        <Slider
          size="small"
          min={0}
          max={0.2}
          step={0.005}
          value={Number(alignSpacing) || 0}
          onChange={(_, v) => onChangeAlignSpacing?.(v)}
          sx={{ color: VC.accent, flex: 1 }}
        />
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: VC.text,
            fontFamily: "monospace",
            minWidth: 36,
          }}
        >
          {Math.round((Number(alignSpacing) || 0) * 1000) / 10}%
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: "0.62rem", color: VC.textDim }}>
        Con Key: gap fijo desde la clave. Sin Key: reparte el hueco (Page =
        bordes de la mesa).
      </Typography>
    </Stack>
  );

  const panelBody = (() => {
    switch (panel) {
      case "color":
      case "colorGuide": // alias legacy → mismo panel Color
        return (
          <Stack spacing={1.1}>
            {sectionLabel(selectionLabel || "Fill / Stroke")}
            {colorPickers}
            {sectionLabel("Weight")}
            <Stack direction="row" spacing={1} alignItems="center">
              <Slider
                size="small"
                min={0}
                max={12}
                step={0.5}
                value={Number(stroke) || 0}
                onChange={(_, v) => onChangeStroke?.(v)}
                sx={{ color: VC.accent, flex: 1 }}
              />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: VC.text,
                  fontFamily: "monospace",
                  minWidth: 28,
                }}
              >
                {Number(stroke) || 0}
              </Typography>
            </Stack>
            {sectionLabel("Swatches")}
            {swatchGrid}
            <Typography sx={{ fontSize: "0.62rem", color: VC.textDim }}>
              Clic = color del target (Fill/Stroke) · derecho = el otro
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => onChangePanel?.("gradient")}
              sx={{
                mt: 0.25,
                border: `1px solid ${VC.panelBorder}`,
                bgcolor: alpha("#fff", 0.04),
                color: VC.text,
                fontSize: "0.72rem",
                fontWeight: 700,
                py: 0.7,
                borderRadius: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
              }}
            >
              <GradientOutlinedIcon sx={{ fontSize: 16 }} />
              Gradient…
            </Typography>
          </Stack>
        );
      case "swatches":
        return (
          <Stack spacing={1}>
            {sectionLabel("Library")}
            {swatchGrid}
            <Typography sx={{ fontSize: "0.68rem", color: VC.textDim }}>
              Clic = target activo · derecho = el otro
            </Typography>
          </Stack>
        );
      case "brushes":
        return (
          <Stack spacing={1}>
            {sectionLabel("Stroke weight")}
            <Slider
              size="small"
              min={0.5}
              max={12}
              step={0.5}
              value={Number(stroke) || 2}
              onChange={(_, v) => onChangeStroke?.(v)}
              sx={{ color: VC.accent }}
            />
            <Typography sx={{ fontSize: "0.7rem", color: VC.text }}>
              {Number(stroke) || 2} px
            </Typography>
            {soon("Pincel / blob: usa la barra Tools (Paintbrush / Blob).")}
          </Stack>
        );
      case "symbols":
        return (
          <Stack spacing={1}>
            {sectionLabel("Symbols")}
            <Typography
              component="button"
              type="button"
              onClick={() => onDefineSymbol?.()}
              sx={{
                border: `1px solid ${VC.panelBorder}`,
                bgcolor: alpha("#fff", 0.04),
                color: VC.text,
                fontSize: "0.72rem",
                py: 0.65,
                borderRadius: 1,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              Define Symbol (desde selección)
            </Typography>
            {(Array.isArray(symbols) ? symbols : []).length ? (
              <Stack spacing={0.5}>
                {symbols.map((s) => {
                  const active = String(s.id) === String(activeSymbolId);
                  return (
                    <Typography
                      key={s.id}
                      component="button"
                      type="button"
                      onClick={() => onSelectSymbol?.(s.id)}
                      sx={{
                        border: `1px solid ${
                          active ? VC.accent : VC.panelBorder
                        }`,
                        bgcolor: active
                          ? alpha("#d9472f", 0.18)
                          : alpha("#fff", 0.03),
                        color: VC.text,
                        fontSize: "0.7rem",
                        py: 0.55,
                        px: 0.75,
                        borderRadius: 1,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {s.name || s.id}
                    </Typography>
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                Sin símbolos · selecciona una forma y Define Symbol
              </Typography>
            )}
            {symbolSprayOpts ? (
              <>
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Radio spray{" "}
                  {Math.round((Number(symbolSprayOpts.radius) || 0.05) * 100)}%
                </Typography>
                <Slider
                  size="small"
                  min={0.02}
                  max={0.15}
                  step={0.01}
                  value={Math.min(
                    0.15,
                    Math.max(0.02, Number(symbolSprayOpts.radius) || 0.05),
                  )}
                  onChange={(_, v) =>
                    onChangeSymbolSprayOpts?.({
                      ...symbolSprayOpts,
                      radius: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Densidad {Math.round(Number(symbolSprayOpts.density) || 3)}
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={8}
                  step={1}
                  value={Math.min(
                    8,
                    Math.max(1, Number(symbolSprayOpts.density) || 3),
                  )}
                  onChange={(_, v) =>
                    onChangeSymbolSprayOpts?.({
                      ...symbolSprayOpts,
                      density: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
              </>
            ) : null}
          </Stack>
        );
      case "patternOptions":
        return (
          <Stack spacing={1}>
            {sectionLabel("Pattern")}
            {soon(
              "Opciones de patrón — usa Rectangular / Polar Grid en Tools.",
            )}
          </Stack>
        );
      case "graphicStyles":
        return (
          <Stack spacing={1}>
            {sectionLabel("Graphic Styles")}
            {colorPickers}
            <Slider
              size="small"
              min={0.05}
              max={1}
              step={0.05}
              value={Math.min(1, Math.max(0.05, Number(opacity) || 1))}
              onChange={(_, v) => onChangeOpacity?.(v)}
              sx={{ color: VC.accent }}
            />
            <Typography
              component="button"
              type="button"
              onClick={() => onApplyGradient?.()}
              sx={{
                border: `1px solid ${VC.panelBorder}`,
                bgcolor: alpha("#fff", 0.04),
                color: VC.text,
                fontSize: "0.72rem",
                py: 0.65,
                borderRadius: 1,
                cursor: "pointer",
              }}
            >
              Style · gradient fill
            </Typography>
          </Stack>
        );
      case "glyphs":
        return (
          <Stack spacing={1}>
            {sectionLabel("Glyphs")}
            {soon("Glifos tipográficos — escribe con Type (T) en Tools.")}
          </Stack>
        );
      case "actions":
        return (
          <Stack spacing={1}>
            {sectionLabel("Actions")}
            <Stack direction="row" gap={0.75}>
              <Typography
                component="button"
                type="button"
                disabled={!canUndo}
                onClick={() => onUndo?.()}
                sx={{
                  flex: 1,
                  border: `1px solid ${VC.panelBorder}`,
                  bgcolor: alpha("#fff", 0.04),
                  color: canUndo ? VC.text : VC.iconDim,
                  fontSize: "0.72rem",
                  py: 0.75,
                  borderRadius: 1,
                  cursor: canUndo ? "pointer" : "default",
                }}
              >
                Undo
              </Typography>
              <Typography
                component="button"
                type="button"
                disabled={!canRedo}
                onClick={() => onRedo?.()}
                sx={{
                  flex: 1,
                  border: `1px solid ${VC.panelBorder}`,
                  bgcolor: alpha("#fff", 0.04),
                  color: canRedo ? VC.text : VC.iconDim,
                  fontSize: "0.72rem",
                  py: 0.75,
                  borderRadius: 1,
                  cursor: canRedo ? "pointer" : "default",
                }}
              >
                Redo
              </Typography>
            </Stack>
          </Stack>
        );
      case "variables":
        return (
          <Stack spacing={1}>
            {sectionLabel("Variables")}
            {(infoLines || []).length
              ? infoLines.map((line) => (
                  <Typography
                    key={line}
                    sx={{ fontSize: "0.72rem", color: VC.text }}
                  >
                    {line}
                  </Typography>
                ))
              : soon("Datos de documento / selección.")}
          </Stack>
        );
      case "stroke":
        return (
          <Stack spacing={1}>
            {sectionLabel("Stroke")}
            <Slider
              size="small"
              min={0}
              max={12}
              step={0.5}
              value={Number(stroke) || 0}
              onChange={(_, v) => onChangeStroke?.(v)}
              sx={{ color: VC.accent }}
            />
            {colorPickers}
          </Stack>
        );
      case "gradient": {
        const gFrom = hexOk(gradient?.from || fillColor);
        const gTo = hexOk(gradient?.to || strokeColor || "#ffffff");
        const gKind =
          String(gradient?.kind || "linear").toLowerCase() === "radial"
            ? "radial"
            : "linear";
        const gAngle = Number.isFinite(Number(gradient?.angle))
          ? Number(gradient.angle)
          : 0;
        const rampCss =
          gKind === "radial"
            ? `radial-gradient(circle at 50% 50%, ${gFrom} 0%, ${gTo} 100%)`
            : `linear-gradient(${90 - gAngle}deg, ${gFrom} 0%, ${gTo} 100%)`;
        return (
          <Stack spacing={1.1}>
            {sectionLabel("Type")}
            <Stack direction="row" gap={0.5}>
              {[
                ["linear", "Linear"],
                ["radial", "Radial"],
              ].map(([id, tip]) => (
                <Tooltip key={id} title={tip}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onChangeGradient?.({ kind: id })}
                    sx={{
                      flex: 1,
                      py: 0.55,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: gKind === id ? VC.accent : VC.panelBorder,
                      bgcolor:
                        gKind === id
                          ? alpha(VC.accent, 0.22)
                          : alpha("#fff", 0.04),
                      color: VC.text,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {tip}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
            {sectionLabel("Stops")}
            <Box
              sx={{
                height: 28,
                borderRadius: 1,
                border: `1px solid ${VC.panelBorder}`,
                background: rampCss,
              }}
            />
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Tooltip title="Stop 1 (start)">
                <Box
                  component="input"
                  type="color"
                  value={gFrom}
                  onChange={(e) => onChangeGradient?.({ from: e.target.value })}
                  sx={{
                    width: 36,
                    height: 28,
                    p: 0,
                    border: `1px solid ${VC.panelBorder}`,
                    borderRadius: 0.75,
                    cursor: "pointer",
                    bgcolor: "transparent",
                  }}
                />
              </Tooltip>
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  color: VC.textDim,
                  fontFamily: "monospace",
                  flex: 1,
                }}
              >
                {gFrom.toUpperCase()} → {gTo.toUpperCase()}
              </Typography>
              <Tooltip title="Stop 2 (end)">
                <Box
                  component="input"
                  type="color"
                  value={gTo}
                  onChange={(e) => onChangeGradient?.({ to: e.target.value })}
                  sx={{
                    width: 36,
                    height: 28,
                    p: 0,
                    border: `1px solid ${VC.panelBorder}`,
                    borderRadius: 0.75,
                    cursor: "pointer",
                    bgcolor: "transparent",
                  }}
                />
              </Tooltip>
            </Stack>
            {gKind === "linear" ? (
              <>
                {sectionLabel("Angle")}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Slider
                    size="small"
                    min={-180}
                    max={180}
                    step={5}
                    value={gAngle}
                    onChange={(_, v) => onChangeGradient?.({ angle: v })}
                    sx={{ color: VC.accent, flex: 1 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      color: VC.text,
                      fontFamily: "monospace",
                      minWidth: 40,
                    }}
                  >
                    {Math.round(gAngle)}°
                  </Typography>
                </Stack>
              </>
            ) : null}
            <Stack direction="row" gap={0.5}>
              <Typography
                component="button"
                type="button"
                onClick={() => onReverseGradient?.()}
                sx={{
                  flex: 1,
                  border: `1px solid ${VC.panelBorder}`,
                  bgcolor: alpha("#fff", 0.04),
                  color: VC.text,
                  fontSize: "0.7rem",
                  py: 0.65,
                  borderRadius: 1,
                  cursor: "pointer",
                }}
              >
                Reverse
              </Typography>
              <Typography
                component="button"
                type="button"
                onClick={() => onApplyGradient?.()}
                sx={{
                  flex: 1,
                  border: `1px solid ${VC.accent}`,
                  bgcolor: alpha(VC.accent, 0.2),
                  color: VC.text,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  py: 0.65,
                  borderRadius: 1,
                  cursor: "pointer",
                }}
              >
                Apply
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: "0.62rem", color: VC.textDim }}>
              Elige los dos colores del degradado (como en VectorCraft). Apply
              lo pone en la marca seleccionada.
            </Typography>
          </Stack>
        );
      }
      case "transparency":
        return (
          <Stack spacing={1}>
            {sectionLabel("Opacity")}
            <Slider
              size="small"
              min={0.05}
              max={1}
              step={0.05}
              value={Math.min(1, Math.max(0.05, Number(opacity) || 1))}
              onChange={(_, v) => onChangeOpacity?.(v)}
              sx={{ color: VC.accent }}
            />
            <Typography sx={{ fontSize: "0.7rem", color: VC.text }}>
              {Math.round((Number(opacity) || 1) * 100)}%
            </Typography>
          </Stack>
        );
      case "appearance":
        return (
          <Stack spacing={1}>
            <Typography sx={{ fontSize: "0.7rem", color: VC.textDim }}>
              {selectionLabel || "Nada seleccionado"}
            </Typography>
            {sectionLabel("Transform")}
            {transformFields}
            {selectedMark?.type === "roundrect" ? (
              <>
                {sectionLabel("Round rect · radio")}
                <TextField
                  size="small"
                  label="rx"
                  type="number"
                  value={Number(selectedMark.rx) || 0.04}
                  onChange={(e) => {
                    const maxR =
                      Math.min(
                        Number(selectedMark.w) || 0,
                        Number(selectedMark.h) || 0,
                      ) / 2;
                    const rx = Math.min(
                      maxR || 0.25,
                      Math.max(0, Number(e.target.value) || 0),
                    );
                    onPatchSelectedMark?.({ rx });
                  }}
                  inputProps={{ min: 0, max: 0.5, step: 0.01 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: alpha("#fff", 0.04),
                      "& fieldset": { borderColor: VC.panelBorder },
                    },
                    "& label": { color: VC.textDim },
                    "& input": { color: VC.text, fontSize: "0.75rem" },
                  }}
                />
              </>
            ) : null}
            {selectedMark?.type === "polygon" ? (
              <>
                {sectionLabel("Polígono · lados")}
                <TextField
                  size="small"
                  label="Lados"
                  type="number"
                  value={Number(selectedMark.sides) || 6}
                  onChange={(e) =>
                    onPatchSelectedMark?.({
                      sides: Math.max(
                        3,
                        Math.min(24, Math.round(Number(e.target.value) || 6)),
                      ),
                    })
                  }
                  inputProps={{ min: 3, max: 24, step: 1 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: alpha("#fff", 0.04),
                      "& fieldset": { borderColor: VC.panelBorder },
                    },
                    "& label": { color: VC.textDim },
                    "& input": { color: VC.text, fontSize: "0.75rem" },
                  }}
                />
              </>
            ) : null}
            {selectedMark?.type === "star" ? (
              <>
                {sectionLabel("Estrella · puntas")}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Puntas"
                    type="number"
                    value={Number(selectedMark.tips) || 5}
                    onChange={(e) =>
                      onPatchSelectedMark?.({
                        tips: Math.max(
                          3,
                          Math.min(16, Math.round(Number(e.target.value) || 5)),
                        ),
                      })
                    }
                    inputProps={{ min: 3, max: 16, step: 1 }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha("#fff", 0.04),
                        "& fieldset": { borderColor: VC.panelBorder },
                      },
                      "& label": { color: VC.textDim },
                      "& input": { color: VC.text, fontSize: "0.75rem" },
                    }}
                  />
                  <TextField
                    size="small"
                    label="Inner"
                    type="number"
                    value={Number(selectedMark.innerRatio) || 0.45}
                    onChange={(e) =>
                      onPatchSelectedMark?.({
                        innerRatio: Math.min(
                          0.9,
                          Math.max(0.15, Number(e.target.value) || 0.45),
                        ),
                      })
                    }
                    inputProps={{ min: 0.15, max: 0.9, step: 0.05 }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha("#fff", 0.04),
                        "& fieldset": { borderColor: VC.panelBorder },
                      },
                      "& label": { color: VC.textDim },
                      "& input": { color: VC.text, fontSize: "0.75rem" },
                    }}
                  />
                </Stack>
              </>
            ) : null}
            {selectedMark?.type === "arc" ? (
              <>
                {sectionLabel("Arco · barrido")}
                <TextField
                  size="small"
                  label="Sweep °"
                  type="number"
                  value={Number(selectedMark.sweep) || 90}
                  onChange={(e) => {
                    let sweep = Number(e.target.value);
                    if (!Number.isFinite(sweep)) sweep = 90;
                    sweep = Math.max(-359, Math.min(359, sweep));
                    if (Math.abs(sweep) < 1) sweep = 90;
                    onPatchSelectedMark?.({ sweep });
                  }}
                  inputProps={{ min: -359, max: 359, step: 5 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: alpha("#fff", 0.04),
                      "& fieldset": { borderColor: VC.panelBorder },
                    },
                    "& label": { color: VC.textDim },
                    "& input": { color: VC.text, fontSize: "0.75rem" },
                  }}
                />
              </>
            ) : null}
            {selectedMark?.type === "dimension" ? (
              <>
                {sectionLabel("Cota · unidad")}
                <Stack direction="row" spacing={1}>
                  <Tooltip title="% del artboard">
                    <IconButton
                      size="small"
                      onClick={() =>
                        onPatchSelectedMark?.({
                          unit: "pct",
                          label: "",
                        })
                      }
                      sx={{
                        color:
                          (selectedMark.unit || "pct") === "pct"
                            ? VC.accent
                            : VC.textDim,
                        border: `1px solid ${
                          (selectedMark.unit || "pct") === "pct"
                            ? VC.accent
                            : VC.panelBorder
                        }`,
                        borderRadius: 1,
                        fontSize: "0.7rem",
                        px: 1,
                      }}
                    >
                      %
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="mm (mesa A4 210×297)">
                    <IconButton
                      size="small"
                      onClick={() =>
                        onPatchSelectedMark?.({
                          unit: "mm",
                          label: "",
                        })
                      }
                      sx={{
                        color:
                          selectedMark.unit === "mm" ? VC.accent : VC.textDim,
                        border: `1px solid ${
                          selectedMark.unit === "mm"
                            ? VC.accent
                            : VC.panelBorder
                        }`,
                        borderRadius: 1,
                        fontSize: "0.7rem",
                        px: 1,
                      }}
                    >
                      mm
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Etiqueta vacía = auto según unidad
                </Typography>
              </>
            ) : null}
            {selectedMark?.type === "grid" ? (
              <>
                {sectionLabel("Grid · divisiones")}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Cols"
                    type="number"
                    value={Number(selectedMark.cols) || 4}
                    onChange={(e) =>
                      onPatchSelectedMark?.({
                        cols: clampGridCols(e.target.value, 4),
                      })
                    }
                    inputProps={{
                      min: GRID_COLS_MIN,
                      max: GRID_COLS_MAX,
                      step: 1,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha("#fff", 0.04),
                        "& fieldset": { borderColor: VC.panelBorder },
                      },
                      "& label": { color: VC.textDim },
                      "& input": { color: VC.text, fontSize: "0.75rem" },
                    }}
                  />
                  <TextField
                    size="small"
                    label="Rows"
                    type="number"
                    value={Number(selectedMark.rows) || 4}
                    onChange={(e) =>
                      onPatchSelectedMark?.({
                        rows: clampGridRows(e.target.value, 4),
                      })
                    }
                    inputProps={{
                      min: GRID_ROWS_MIN,
                      max: GRID_ROWS_MAX,
                      step: 1,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha("#fff", 0.04),
                        "& fieldset": { borderColor: VC.panelBorder },
                      },
                      "& label": { color: VC.textDim },
                      "& input": { color: VC.text, fontSize: "0.75rem" },
                    }}
                  />
                </Stack>
              </>
            ) : null}
            {selectedMark?.type === "polarGrid" ||
            selectedMark?.type === "flare" ? (
              <>
                {sectionLabel(
                  selectedMark.type === "flare"
                    ? "Flare · divisiones"
                    : "Polar · divisiones",
                )}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Anillos"
                    type="number"
                    value={Number(selectedMark.rings) || 4}
                    onChange={(e) =>
                      onPatchSelectedMark?.({
                        rings: clampPolarRings(e.target.value, 4),
                      })
                    }
                    inputProps={{
                      min: POLAR_RINGS_MIN,
                      max: POLAR_RINGS_MAX,
                      step: 1,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha("#fff", 0.04),
                        "& fieldset": { borderColor: VC.panelBorder },
                      },
                      "& label": { color: VC.textDim },
                      "& input": { color: VC.text, fontSize: "0.75rem" },
                    }}
                  />
                  <TextField
                    size="small"
                    label="Rayos"
                    type="number"
                    value={Number(selectedMark.rays) || 8}
                    onChange={(e) =>
                      onPatchSelectedMark?.({
                        rays: clampPolarRays(e.target.value, 8),
                      })
                    }
                    inputProps={{
                      min: POLAR_RAYS_MIN,
                      max: POLAR_RAYS_MAX,
                      step: 1,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: alpha("#fff", 0.04),
                        "& fieldset": { borderColor: VC.panelBorder },
                      },
                      "& label": { color: VC.textDim },
                      "& input": { color: VC.text, fontSize: "0.75rem" },
                    }}
                  />
                </Stack>
              </>
            ) : null}
            {wandOpts && activeMarkTool === "magicWand" ? (
              <>
                {sectionLabel("Varita · tolerancia")}
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  {(
                    Math.min(
                      0.35,
                      Math.max(0, Number(wandOpts.tolerance) || 0),
                    ) * 100
                  ).toFixed(0)}
                  % color
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={0.35}
                  step={0.02}
                  value={Math.min(
                    0.35,
                    Math.max(0, Number(wandOpts.tolerance) || 0),
                  )}
                  onChange={(_, v) =>
                    onChangeWandOpts?.({
                      ...wandOpts,
                      tolerance: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
              </>
            ) : null}
            {smoothOpts ? (
              <>
                {sectionLabel("Suavizar · pasadas")}
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  {Math.round(Number(smoothOpts.passes) || 2)} · pulsa Suavizar
                  en Tools
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={6}
                  step={1}
                  value={Math.min(
                    6,
                    Math.max(1, Math.round(Number(smoothOpts.passes) || 2)),
                  )}
                  onChange={(_, v) =>
                    onChangeSmoothOpts?.({
                      ...smoothOpts,
                      passes: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
              </>
            ) : null}
            {envelopeOpts ? (
              <>
                {sectionLabel("Envelope · Warp")}
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Estilo {String(envelopeOpts.style || "arc")}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={14}
                  step={1}
                  value={Math.max(
                    0,
                    [
                      "arc",
                      "arcLower",
                      "arcUpper",
                      "arch",
                      "bulge",
                      "shellLower",
                      "shellUpper",
                      "flag",
                      "wave",
                      "fish",
                      "rise",
                      "fisheye",
                      "inflate",
                      "squeeze",
                      "twist",
                    ].indexOf(String(envelopeOpts.style || "arc")),
                  )}
                  onChange={(_, v) => {
                    const styles = [
                      "arc",
                      "arcLower",
                      "arcUpper",
                      "arch",
                      "bulge",
                      "shellLower",
                      "shellUpper",
                      "flag",
                      "wave",
                      "fish",
                      "rise",
                      "fisheye",
                      "inflate",
                      "squeeze",
                      "twist",
                    ];
                    onChangeEnvelopeOpts?.({
                      ...envelopeOpts,
                      style: styles[v] || "arc",
                    });
                  }}
                  sx={{ color: VC.accent }}
                />
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Bend {Math.round(Number(envelopeOpts.bend) || 50)}%
                </Typography>
                <Slider
                  size="small"
                  min={-100}
                  max={100}
                  step={1}
                  value={Math.min(
                    100,
                    Math.max(-100, Number(envelopeOpts.bend) || 50),
                  )}
                  onChange={(_, v) =>
                    onChangeEnvelopeOpts?.({
                      ...envelopeOpts,
                      bend: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onApplyEnvelopeWarp?.()}
                  sx={{
                    mt: 0.5,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    borderColor: VC.border,
                    color: VC.text,
                  }}
                >
                  Aplicar Warp
                </Button>
                {sectionLabel("Envelope · Mesh")}
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Filas {Math.round(Number(envelopeOpts.rows) || 3)}
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={8}
                  step={1}
                  value={Math.min(
                    8,
                    Math.max(1, Number(envelopeOpts.rows) || 3),
                  )}
                  onChange={(_, v) =>
                    onChangeEnvelopeOpts?.({
                      ...envelopeOpts,
                      rows: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Columnas {Math.round(Number(envelopeOpts.cols) || 4)}
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={8}
                  step={1}
                  value={Math.min(
                    8,
                    Math.max(1, Number(envelopeOpts.cols) || 4),
                  )}
                  onChange={(_, v) =>
                    onChangeEnvelopeOpts?.({
                      ...envelopeOpts,
                      cols: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onApplyEnvelopeMesh?.()}
                  sx={{
                    mt: 0.5,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    borderColor: VC.border,
                    color: VC.text,
                  }}
                >
                  Aplicar Mesh
                </Button>
              </>
            ) : null}
            {widthOpts && activeMarkTool === "width" ? (
              <>
                {sectionLabel("Anchura · paso")}
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Perfil VC · ±{(Number(widthOpts.step) || 0.5).toFixed(1)} por
                  gesto · Alt = más fino
                </Typography>
                <Slider
                  size="small"
                  min={0.15}
                  max={2}
                  step={0.05}
                  value={Math.min(
                    2,
                    Math.max(0.15, Number(widthOpts.step) || 0.5),
                  )}
                  onChange={(_, v) =>
                    onChangeWidthOpts?.({
                      ...widthOpts,
                      step: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
              </>
            ) : null}
            {liquifyOpts &&
            [
              "warp",
              "twirl",
              "pucker",
              "bloat",
              "scallop",
              "crystallize",
              "wrinkle",
              "reshape",
            ].includes(activeMarkTool) ? (
              <>
                {sectionLabel(`Liquify · ${activeMarkTool}`)}
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Radio {Math.round((Number(liquifyOpts.radius) || 0.09) * 100)}
                  %
                </Typography>
                <Slider
                  size="small"
                  min={0.03}
                  max={0.35}
                  step={0.01}
                  value={Math.min(
                    0.35,
                    Math.max(0.03, Number(liquifyOpts.radius) || 0.09),
                  )}
                  onChange={(_, v) =>
                    onChangeLiquifyOpts?.({
                      ...liquifyOpts,
                      radius: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Fuerza{" "}
                  {Math.round((Number(liquifyOpts.strength) || 0.4) * 100)}%
                </Typography>
                <Slider
                  size="small"
                  min={0.1}
                  max={1.2}
                  step={0.05}
                  value={Math.min(
                    1.2,
                    Math.max(0.1, Number(liquifyOpts.strength) || 0.4),
                  )}
                  onChange={(_, v) =>
                    onChangeLiquifyOpts?.({
                      ...liquifyOpts,
                      strength: v,
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                {activeMarkTool === "twirl" ? (
                  <>
                    <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                      Ángulo {Math.round(Number(liquifyOpts.angleDeg) || 28)}°
                    </Typography>
                    <Slider
                      size="small"
                      min={5}
                      max={90}
                      step={1}
                      value={Math.min(
                        90,
                        Math.max(5, Number(liquifyOpts.angleDeg) || 28),
                      )}
                      onChange={(_, v) =>
                        onChangeLiquifyOpts?.({
                          ...liquifyOpts,
                          angleDeg: v,
                        })
                      }
                      sx={{ color: VC.accent }}
                    />
                  </>
                ) : null}
              </>
            ) : null}
            {sectionLabel("Appearance")}
            {colorPickers}
            <TextField
              size="small"
              label="Stroke"
              type="number"
              value={stroke}
              onChange={(e) =>
                onChangeStroke?.(
                  Math.min(12, Math.max(0, Number(e.target.value) || 0)),
                )
              }
              inputProps={{ min: 0, max: 12, step: 0.5 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha("#fff", 0.04),
                  "& fieldset": { borderColor: VC.panelBorder },
                },
                "& label": { color: VC.textDim },
                "& input": { color: VC.text, fontSize: "0.75rem" },
              }}
            />
            {sectionLabel("Layers")}
            <Stack spacing={0.25} sx={{ maxHeight: 160, overflowY: "auto" }}>
              {!layerItems.length
                ? soon("Sin capas en esta página")
                : layerItems.map((it) => (
                    <Box
                      key={`${it.kind}-${it.id}`}
                      onClick={() => onSelectLayer?.(it.id, it.kind)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 0.75,
                        py: 0.45,
                        borderRadius: 0.75,
                        cursor: "pointer",
                        bgcolor: it.selected ? VC.activeBg : "transparent",
                        color: it.selected ? VC.accent : VC.text,
                        "&:hover": {
                          bgcolor: it.selected ? VC.activeBg : VC.hover,
                        },
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontSize: "0.7rem",
                          flex: 1,
                          fontWeight: it.selected ? 700 : 500,
                        }}
                      >
                        {it.label}
                      </Typography>
                    </Box>
                  ))}
            </Stack>
          </Stack>
        );
      case "artboards":
        return (
          <Stack spacing={1}>
            <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
              Orden = PDF. ↑↓ recoloca en el tablero. Arrastra la barra de cada
              mesa para moverla a mano.
            </Typography>
            <Stack spacing={0.35}>
              {Array.from({ length: Math.max(1, pageCount) }, (_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="center"
                  spacing={0.25}
                  draggable={Boolean(onReorderPage) && pageCount > 1}
                  onDragStart={(e) => {
                    if (!onReorderPage || pageCount <= 1) return;
                    e.dataTransfer.setData("text/artboard-index", String(i));
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    if (!onReorderPage || pageCount <= 1) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    if (!onReorderPage || pageCount <= 1) return;
                    e.preventDefault();
                    const raw = e.dataTransfer.getData("text/artboard-index");
                    const from = Math.floor(Number(raw));
                    if (!Number.isFinite(from) || from === i) return;
                    onReorderPage(from, i);
                  }}
                  sx={{
                    borderRadius: 0.75,
                    bgcolor: i === pageIdx ? VC.activeBg : "transparent",
                    "&:hover": { bgcolor: VC.hover },
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onSelectPage?.(i)}
                    sx={{
                      flex: 1,
                      textAlign: "left",
                      border: "none",
                      bgcolor: "transparent",
                      borderRadius: 0.75,
                      px: 0.75,
                      py: 0.55,
                      cursor:
                        onReorderPage && pageCount > 1 ? "grab" : "pointer",
                      color: i === pageIdx ? VC.accent : VC.text,
                      fontSize: "0.75rem",
                      fontWeight: i === pageIdx ? 700 : 500,
                    }}
                  >
                    Artboard {i + 1}
                  </Box>
                  {onReorderPage && pageCount > 1 ? (
                    <Stack direction="row" sx={{ pr: 0.25 }}>
                      <IconButton
                        size="small"
                        disabled={i === 0}
                        aria-label={`Subir artboard ${i + 1}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReorderPage(i, i - 1);
                        }}
                        sx={{
                          color: VC.icon,
                          p: 0.25,
                          opacity: i === 0 ? 0.35 : 1,
                        }}
                      >
                        <KeyboardArrowUpOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={i >= pageCount - 1}
                        aria-label={`Bajar artboard ${i + 1}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReorderPage(i, i + 1);
                        }}
                        sx={{
                          color: VC.icon,
                          p: 0.25,
                          opacity: i >= pageCount - 1 ? 0.35 : 1,
                        }}
                      >
                        <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  ) : null}
                </Stack>
              ))}
            </Stack>
            {onRearrangeArtboards && pageCount > 1 ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <DashboardCustomizeOutlinedIcon sx={{ fontSize: 16 }} />
                }
                onClick={() => onRearrangeArtboards()}
                sx={{
                  textTransform: "none",
                  fontSize: "0.7rem",
                  borderColor: VC.panelBorder,
                  color: VC.text,
                  justifyContent: "flex-start",
                }}
              >
                Rearrange All
              </Button>
            ) : null}
            {sectionLabel("Perspective Grid")}
            <Button
              size="small"
              variant="outlined"
              onClick={() => onTogglePerspectiveGrid?.()}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                borderColor: VC.border,
                color: VC.text,
              }}
            >
              {perspectiveGrid?.visible ? "Ocultar grid" : "Mostrar grid"}
            </Button>
            {perspectiveGrid ? (
              <>
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Modo{" "}
                  {perspectiveGrid.mode === "1point" ? "1 punto" : "2 puntos"}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={1}
                  step={1}
                  value={perspectiveGrid.mode === "1point" ? 0 : 1}
                  onChange={(_, v) =>
                    onChangePerspectiveGrid?.({
                      mode: v === 0 ? "1point" : "2point",
                    })
                  }
                  sx={{ color: VC.accent }}
                />
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Plano activo: {String(perspectiveGrid.plane || "ground")}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {["ground", "left", "right", "none"].map((pl) => (
                    <Button
                      key={pl}
                      size="small"
                      variant={
                        perspectiveGrid.plane === pl ? "contained" : "outlined"
                      }
                      onClick={() => onChangePerspectiveGrid?.({ plane: pl })}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.65rem",
                        minWidth: 0,
                        px: 1,
                        bgcolor:
                          perspectiveGrid.plane === pl
                            ? VC.accent
                            : "transparent",
                        borderColor: VC.border,
                        color: perspectiveGrid.plane === pl ? "#fff" : VC.text,
                      }}
                    >
                      {pl}
                    </Button>
                  ))}
                </Stack>
                <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                  Celdas {Math.round(Number(perspectiveGrid.cells) || 6)}
                </Typography>
                <Slider
                  size="small"
                  min={3}
                  max={12}
                  step={1}
                  value={Math.min(
                    12,
                    Math.max(3, Number(perspectiveGrid.cells) || 6),
                  )}
                  onChange={(_, v) => onChangePerspectiveGrid?.({ cells: v })}
                  sx={{ color: VC.accent }}
                />
              </>
            ) : (
              <Typography sx={{ fontSize: "0.65rem", color: VC.textDim }}>
                Shift+P o Mostrar grid para activar
              </Typography>
            )}
          </Stack>
        );
      case "transform":
        return (
          <Stack spacing={1}>
            {sectionLabel(selectionLabel || "Transform")}
            {transformFields}
          </Stack>
        );
      case "align":
        return alignBody;
      case "pathfinder":
        return (
          <Stack spacing={1}>
            {sectionLabel("Pathfinder")}
            <Typography
              sx={{ color: VC.textDim, fontSize: "0.68rem", px: 0.25 }}
            >
              Shape modes · 2+ marks
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {[
                ["unite", "Unite", "Unir contornos (2 formas)"],
                ["minus", "Minus", "1ª − 2ª (orden Shift+clic)"],
                ["intersect", "Intersect", "Solo solape real"],
                ["exclude", "Exclude", "XOR de contornos"],
              ].map(([op, label, tip]) => (
                <Tooltip key={op} title={tip}>
                  <IconButton
                    size="small"
                    onClick={() => onPathfinder?.(op)}
                    sx={{
                      color: VC.icon,
                      bgcolor: alpha("#fff", 0.04),
                      border: `1px solid ${VC.panelBorder}`,
                      fontSize: "0.65rem",
                      borderRadius: 1,
                      px: 1,
                      height: 28,
                      width: "auto",
                      "&:hover": { bgcolor: VC.hover },
                    }}
                  >
                    {label}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Stack>
        );
      case "character":
        return (
          <Stack spacing={1}>
            {sectionLabel("Character")}
            <TextField
              size="small"
              label="Size"
              type="number"
              inputProps={{
                min: MARK_FONT_SIZE_MIN,
                max: MARK_FONT_SIZE_MAX,
                step: 1,
              }}
              value={fontSize}
              onChange={(e) =>
                onChangeFontSize?.(clampMarkFontSize(e.target.value, 14))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha("#fff", 0.04),
                  "& fieldset": { borderColor: VC.panelBorder },
                },
                "& label": { color: VC.textDim },
                "& input": { color: VC.text, fontSize: "0.75rem" },
              }}
            />
            <TextField
              select
              size="small"
              label="Family"
              value={fontFamily || "sans"}
              onChange={(e) => onChangeFontFamily?.(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha("#fff", 0.04),
                  "& fieldset": { borderColor: VC.panelBorder },
                },
                "& label": { color: VC.textDim },
                "& .MuiSelect-select": {
                  color: VC.text,
                  fontSize: "0.75rem",
                },
              }}
            >
              {markFontGroups().flatMap((group) =>
                group.families.map((family) => (
                  <MenuItem
                    key={family.id}
                    value={family.id}
                    sx={{ fontFamily: family.css }}
                  >
                    {family.label}
                  </MenuItem>
                )),
              )}
            </TextField>
            <TextField
              select
              size="small"
              label="Weight"
              value={Number(fontWeight) >= 600 ? 700 : 400}
              onChange={(e) => onChangeFontWeight?.(Number(e.target.value))}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha("#fff", 0.04),
                  "& fieldset": { borderColor: VC.panelBorder },
                },
                "& label": { color: VC.textDim },
                "& .MuiSelect-select": {
                  color: VC.text,
                  fontSize: "0.75rem",
                },
              }}
            >
              <MenuItem value={400}>Regular</MenuItem>
              <MenuItem value={700}>Bold</MenuItem>
            </TextField>
            <Typography
              sx={{ fontSize: "0.62rem", color: VC.textDim, px: 0.25 }}
            >
              Contorno (stroke) · {Number(stroke) || 0}
            </Typography>
            <Slider
              size="small"
              min={0}
              max={12}
              step={0.5}
              value={Number(stroke) || 0}
              onChange={(_, v) => onChangeStroke?.(Number(v))}
              sx={{
                color: VC.accent,
                "& .MuiSlider-rail": { opacity: 0.35 },
              }}
            />
            {colorPickers}
            <Typography
              sx={{ fontSize: "0.6rem", color: VC.textDim, px: 0.25 }}
            >
              Fill = color de letra · Stroke = contorno. En edición se actualiza
              al vuelo.
            </Typography>
          </Stack>
        );
      case "paragraph":
        return (
          <Stack spacing={1}>
            {sectionLabel("Paragraph")}
            <Typography
              sx={{ color: VC.textDim, fontSize: "0.68rem", px: 0.25 }}
            >
              Align
            </Typography>
            <Stack direction="row" gap={0.5}>
              {[
                {
                  id: "left",
                  Icon: FormatAlignLeftOutlinedIcon,
                  label: "Left",
                },
                {
                  id: "center",
                  Icon: FormatAlignCenterOutlinedIcon,
                  label: "Center",
                },
                {
                  id: "right",
                  Icon: FormatAlignRightOutlinedIcon,
                  label: "Right",
                },
              ].map(({ id, Icon, label }) => {
                const active = String(textAlign || "left") === id;
                return (
                  <Tooltip key={id} title={label}>
                    <IconButton
                      size="small"
                      onClick={() => onChangeTextAlign?.(id)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: active ? VC.accent : VC.icon,
                        bgcolor: active ? VC.activeBg : alpha("#fff", 0.04),
                        border: `1px solid ${VC.panelBorder}`,
                        "&:hover": {
                          bgcolor: active ? VC.activeBg : VC.hover,
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Stack>
          </Stack>
        );
      case "history":
        return (
          <Stack spacing={1}>
            <Stack direction="row" gap={0.75}>
              <Typography
                component="button"
                type="button"
                disabled={!canUndo}
                onClick={() => onUndo?.()}
                sx={{
                  flex: 1,
                  border: `1px solid ${VC.panelBorder}`,
                  bgcolor: alpha("#fff", 0.04),
                  color: canUndo ? VC.text : VC.iconDim,
                  fontSize: "0.72rem",
                  py: 0.75,
                  borderRadius: 1,
                  cursor: canUndo ? "pointer" : "default",
                }}
              >
                Undo
              </Typography>
              <Typography
                component="button"
                type="button"
                disabled={!canRedo}
                onClick={() => onRedo?.()}
                sx={{
                  flex: 1,
                  border: `1px solid ${VC.panelBorder}`,
                  bgcolor: alpha("#fff", 0.04),
                  color: canRedo ? VC.text : VC.iconDim,
                  fontSize: "0.72rem",
                  py: 0.75,
                  borderRadius: 1,
                  cursor: canRedo ? "pointer" : "default",
                }}
              >
                Redo
              </Typography>
            </Stack>
          </Stack>
        );
      case "info":
        return (
          <Stack spacing={1}>
            {sectionLabel("Selection")}
            {(infoLines || []).length
              ? infoLines.map((line) => (
                  <Typography
                    key={line}
                    sx={{ fontSize: "0.72rem", color: VC.text }}
                  >
                    {line}
                  </Typography>
                ))
              : soon("Info de selección / zoom.")}
            {sectionLabel("Clipping mask")}
            <Typography
              sx={{
                fontSize: "0.68rem",
                color: VC.textDim,
                lineHeight: 1.45,
                whiteSpace: "pre-line",
              }}
            >
              {[
                "1. Coloca una foto en la página.",
                "2. Dibuja una forma encima (rect / elipse / path…).",
                "3. Selecciona foto + forma (Shift) → Make Clipping Mask (Ctrl+7) o menú contextual.",
                "4. Liberar: selecciona la foto → Release (Ctrl+Alt+7).",
                "La máscara viaja con el marco; no sale en PDF si la foto está solo en el pasteboard.",
              ].join("\n")}
            </Typography>
            {sectionLabel("Free Transform (E)")}
            <Typography
              sx={{ fontSize: "0.68rem", color: VC.textDim, lineHeight: 1.45 }}
            >
              Esquinas = escala · asa superior = rotar · Alt+lado = shear ·
              Shift = proporcional. Vale para fotos y marcas.
            </Typography>
          </Stack>
        );
      case "navigator":
        return (
          <Stack spacing={0.5}>
            {(infoLines || []).length
              ? infoLines.map((line) => (
                  <Typography
                    key={`nav-${line}`}
                    sx={{ fontSize: "0.72rem", color: VC.text }}
                  >
                    {line}
                  </Typography>
                ))
              : soon("Info de selección / zoom.")}
          </Stack>
        );
      default:
        return soon();
    }
  })();

  return (
    <Box
      data-vc-rightdock="1"
      sx={{
        display: "flex",
        flexDirection: "row",
        flexShrink: 0,
        alignSelf: "stretch",
        zIndex: 5,
      }}
    >
      {open ? (
        <Box
          sx={{
            width: PANEL_W,
            flexShrink: 0,
            bgcolor: VC.panel,
            borderLeft: `1px solid ${VC.panelBorder}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            color: VC.text,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1,
              py: 0.75,
              borderBottom: `1px solid ${VC.panelBorder}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 0.06,
                color: VC.textDim,
              }}
            >
              {title}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onChangePanel?.(null)}
              sx={{ color: VC.iconDim, p: 0.25 }}
            >
              <CloseOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
            {panelBody}
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          width: DOCK_W,
          flexShrink: 0,
          bgcolor: VC.panel,
          borderLeft: `1px solid ${VC.panelBorder}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 0.75,
          gap: 0,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {VC_DOCK_GROUPS.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 ? (
              <Box
                sx={{
                  width: 18,
                  height: 1,
                  my: 0.45,
                  bgcolor: alpha("#fff", 0.1),
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Stack spacing={0.15} alignItems="center">
              {group.map((p) => (
                <DockBtn key={p.id} id={p.id} Icon={p.Icon} label={p.label} />
              ))}
            </Stack>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
